import {
  bytesToArrayBuffer,
  canonicalJson,
  didFromPublicJwk,
  fromBase64Url,
  normalizeEmail,
  textEncoder,
} from "./protocol";

export const EMAIL_VERIFICATION_REQUEST_DOMAIN = "vibe.attester.email.request.v1";
export const VERIFIED_EMAIL_CREDENTIAL_DOMAIN = "vibe.credential.verified-email.v1";
export const VERIFIED_EMAIL_CREDENTIAL_TYPE = "vibe.credential.verified-email.v1" as const;

export interface EmailVerificationRequestPayloadV1 {
  version: 1;
  subjectDid: string;
  email: string;
  requestedAt: string;
  nonce: string;
}

export interface EmailVerificationRequestV1 extends EmailVerificationRequestPayloadV1 {
  proof: {
    algorithm: "ES256";
    encoding: "p1363";
    publicJwk: JsonWebKey;
    value: string;
  };
}

export interface AttesterProfileV1 {
  version: 1;
  name: string;
  issuerDid: string;
  keyId: string;
  publicJwk: JsonWebKey;
}

export interface VerifiedEmailCredentialPayloadV1 {
  version: 1;
  type: typeof VERIFIED_EMAIL_CREDENTIAL_TYPE;
  id: string;
  issuerDid: string;
  subjectDid: string;
  claim: { email: string };
  evidence: {
    method: "email_otp";
    verificationId: string;
    verifiedAt: string;
  };
  issuedAt: string;
  expiresAt: string;
}

export interface VerifiedEmailCredentialV1 extends VerifiedEmailCredentialPayloadV1 {
  signature: {
    algorithm: "ES256";
    encoding: "p1363";
    keyId: string;
    value: string;
  };
}

export function emailVerificationRequestPayload(
  request: EmailVerificationRequestV1 | EmailVerificationRequestPayloadV1,
): EmailVerificationRequestPayloadV1 {
  return {
    version: 1,
    subjectDid: request.subjectDid,
    email: request.email,
    requestedAt: request.requestedAt,
    nonce: request.nonce,
  };
}

export function emailVerificationRequestSigningInput(
  request: EmailVerificationRequestV1 | EmailVerificationRequestPayloadV1,
): string {
  return `${EMAIL_VERIFICATION_REQUEST_DOMAIN}\n${canonicalJson(emailVerificationRequestPayload(request) as never)}`;
}

export function verifiedEmailCredentialPayload(
  credential: VerifiedEmailCredentialV1 | VerifiedEmailCredentialPayloadV1,
): VerifiedEmailCredentialPayloadV1 {
  return {
    version: 1,
    type: VERIFIED_EMAIL_CREDENTIAL_TYPE,
    id: credential.id,
    issuerDid: credential.issuerDid,
    subjectDid: credential.subjectDid,
    claim: { email: credential.claim.email },
    evidence: {
      method: "email_otp",
      verificationId: credential.evidence.verificationId,
      verifiedAt: credential.evidence.verifiedAt,
    },
    issuedAt: credential.issuedAt,
    expiresAt: credential.expiresAt,
  };
}

export function verifiedEmailCredentialSigningInput(
  credential: VerifiedEmailCredentialV1 | VerifiedEmailCredentialPayloadV1,
): string {
  return `${VERIFIED_EMAIL_CREDENTIAL_DOMAIN}\n${canonicalJson(verifiedEmailCredentialPayload(credential) as never)}`;
}

export async function verifyEmailVerificationRequest(request: EmailVerificationRequestV1): Promise<boolean> {
  try {
    if (request.version !== 1 || request.proof.algorithm !== "ES256" || request.proof.encoding !== "p1363") return false;
    if (normalizeEmail(request.email) !== request.email || didFromPublicJwk(request.proof.publicJwk) !== request.subjectDid) return false;
    const key = await crypto.subtle.importKey(
      "jwk",
      request.proof.publicJwk,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    return crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      key,
      bytesToArrayBuffer(fromBase64Url(request.proof.value)),
      textEncoder.encode(emailVerificationRequestSigningInput(request)),
    );
  } catch {
    return false;
  }
}

export async function verifyVerifiedEmailCredential(
  credential: VerifiedEmailCredentialV1,
  trustedIssuer: AttesterProfileV1,
  at = new Date(),
): Promise<boolean> {
  try {
    if (
      credential.version !== 1 ||
      credential.type !== VERIFIED_EMAIL_CREDENTIAL_TYPE ||
      credential.signature.algorithm !== "ES256" ||
      credential.signature.encoding !== "p1363" ||
      credential.signature.keyId !== trustedIssuer.keyId ||
      credential.issuerDid !== trustedIssuer.issuerDid ||
      trustedIssuer.version !== 1 ||
      didFromPublicJwk(trustedIssuer.publicJwk) !== trustedIssuer.issuerDid ||
      !credential.subjectDid.startsWith("did:vibe:p256:") ||
      credential.evidence.method !== "email_otp" ||
      normalizeEmail(credential.claim.email) !== credential.claim.email ||
      !validIsoInstant(credential.issuedAt) ||
      !validIsoInstant(credential.expiresAt) ||
      !validIsoInstant(credential.evidence.verifiedAt) ||
      credential.evidence.verifiedAt !== credential.issuedAt ||
      Date.parse(credential.expiresAt) <= Date.parse(credential.issuedAt) ||
      at.getTime() < Date.parse(credential.issuedAt) ||
      at.getTime() >= Date.parse(credential.expiresAt)
    ) {
      return false;
    }
    const key = await crypto.subtle.importKey(
      "jwk",
      trustedIssuer.publicJwk,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    return crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      key,
      bytesToArrayBuffer(fromBase64Url(credential.signature.value)),
      textEncoder.encode(verifiedEmailCredentialSigningInput(credential)),
    );
  } catch {
    return false;
  }
}

function validIsoInstant(value: string): boolean {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}
